import dayjs, { Dayjs } from "dayjs";
import {
  FoodItem,
  FoodItemNutrition,
  FoodItemSupplierInfo,
} from "client/jspPlatformExperiment";

/**
 * The "Modified on … by …" pill needs a single date+user for an ingredient.
 * Unlike a recipe (which has a backend-computed `updated_at_aggregated`),
 * an ingredient's freshness is spread across its food row, its nutrition
 * records, and its supplier rows — each with their own `updated_at`. This
 * type carries the rolled-up answer.
 */
export interface FoodUpdateInfo {
  date: string | null;
  userFullName: string | null;
}

const parseDate = (v: string | null | undefined): Dayjs | null =>
  v ? dayjs(v) : null;

const latestOf = (dates: Array<Dayjs | null>): Dayjs | null =>
  dates
    .filter((d): d is Dayjs => d != null)
    .reduce<Dayjs | null>((acc, d) => (!acc || d.isAfter(acc) ? d : acc), null);

/**
 * Aggregate one food item's freshness across its food row, nutrition
 * records, and supplier rows. Pass explicit arrays when the caller has
 * them as separate Redux selectors; otherwise the food's inline
 * `food_nutrition_records` and `food_suppliers` are used. The user name
 * comes from the food item itself (nutrition/supplier rows don't carry
 * user info).
 */
export const aggregateFoodUpdateInfo = (
  food: FoodItem | null | undefined,
  nutritions?: FoodItemNutrition[] | null,
  suppliers?: FoodItemSupplierInfo[] | null,
): FoodUpdateInfo => {
  if (!food) {
    return { date: null, userFullName: null };
  }
  const candidates: Array<Dayjs | null> = [parseDate(food.updated_at)];
  (nutritions ?? food.food_nutrition_records ?? []).forEach((n) => {
    candidates.push(parseDate(n.updated_at));
  });
  (suppliers ?? food.food_suppliers ?? []).forEach((s) => {
    candidates.push(parseDate(s.updated_at));
  });
  const latest = latestOf(candidates);
  return {
    date: latest ? latest.toISOString() : null,
    userFullName: food.updated_by_user?.full_name ?? null,
  };
};

/**
 * Reduce a list of per-food aggregates to the one with the latest date.
 * Used by the multi-supplier (ingredient family) page: each family member
 * is aggregated individually, then the latest is shown — and the user
 * name shown is the user who triggered that latest update.
 */
export const latestUpdateInfo = (infos: FoodUpdateInfo[]): FoodUpdateInfo => {
  return infos.reduce<FoodUpdateInfo>(
    (acc, cur) => {
      if (!cur.date) {
        return acc;
      }
      if (!acc.date) {
        return cur;
      }
      return dayjs(cur.date).isAfter(dayjs(acc.date)) ? cur : acc;
    },
    { date: null, userFullName: null },
  );
};
